import React, { useEffect, useState } from "react";
import { CalendarDays, CheckSquare, Inbox, BellRing } from "lucide-react";
import { endpoints } from "@/lib/api";
import { auth } from "@/lib/auth";

const StatPill = ({ icon: Icon, value, label, tone, testId }) => (
  <div
    data-testid={testId}
    className="flex items-center gap-3 rounded-2xl bg-white/70 dark:bg-slate-900/50 border border-slate-200/70 dark:border-white/10 backdrop-blur-md px-4 py-3 min-w-[150px]"
  >
    <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone}`}>
      <Icon className="h-4.5 w-4.5" />
    </div>
    <div className="leading-tight">
      <div className="text-lg font-bold font-display">{value}</div>
      <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

export default function WelcomeBanner({ refreshKey }) {
  const [user, setUser] = useState({ name: auth.current()?.full_name || "", role: "" });
  const [summary, setSummary] = useState({ pending_tasks: 0, appointments_today: 0, open_requests: 0, active_reminders: 0 });
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = () => endpoints.me().then(setUser).catch(() => {});
    load();
    window.addEventListener("isoquest:account-changed", load);
    return () => window.removeEventListener("isoquest:account-changed", load);
  }, []);

  useEffect(() => {
    endpoints.summary().then(setSummary).catch(() => {});
  }, [refreshKey]);

  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = user.name?.split(" ")[0] || "there";
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      data-testid="welcome-banner"
      className="relative overflow-hidden rounded-3xl brand-gradient text-white shadow-xl shadow-indigo-500/20"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-white/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-purple-300/30 blur-3xl" />
      </div>
      <div className="relative p-6 sm:p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
            {dateStr} · {timeStr}
          </div>
          <h1 data-testid="welcome-banner-user-name" className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold mt-2 tracking-tight">
            {greeting}, {firstName}
            <span className="text-white/70"> 👋</span>
          </h1>
          <p className="mt-2 text-sm sm:text-[15px] text-white/85 max-w-xl leading-relaxed">
            Here&apos;s a quick look at your day at ISOQuest. Stay on top of audits, tasks and client requests — all in one place.
          </p>
        </div>
        <div className="lg:col-span-6 flex flex-wrap gap-3 lg:justify-end">
          <StatPill testId="stat-pending-tasks" icon={CheckSquare} value={summary.pending_tasks} label="Pending tasks" tone="bg-indigo-100 text-indigo-700" />
          <StatPill testId="stat-appointments-today" icon={CalendarDays} value={summary.appointments_today} label="Today" tone="bg-blue-100 text-blue-700" />
          <StatPill testId="stat-open-requests" icon={Inbox} value={summary.open_requests} label="Requests" tone="bg-purple-100 text-purple-700" />
          <StatPill testId="stat-active-reminders" icon={BellRing} value={summary.active_reminders} label="Reminders" tone="bg-amber-100 text-amber-700" />
        </div>
      </div>
    </div>
  );
}
